import React, { useEffect, useState } from "react";
import { Plus, Trash2 } from "@/components/icons";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { MoneyInput } from "@/components/MoneyInput";
import { parseAmount } from "@/lib/money";
import { InlineEmpty } from "@/components/ListState";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PARTY = [
  { v: "ons", l: "Ons" },
  { v: "verhuurder", l: "Verhuurder" },
];

// Reusable editor for a list of demarcation rows.
export const DemarcationEditor = ({ rows, onChange, writable }) => {
  const update = (i, key, val) => onChange(rows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => onChange([...rows, { element: "", small_maintenance: "ons", replacement: "ons", note: "" }]);

  return (
    <div>
      {rows.length === 0 ? (
        <InlineEmpty
          testid="demarcation-empty"
          title="Nog geen regels"
          purpose="Zet hier per deel van het pand wie het klein onderhoud doet en wie de vervanging betaalt. Bij een reparatie op een gehuurde locatie bepaalt deze lijst of je de kosten kunt verhalen."
          action={writable ? { label: "Regel toevoegen", icon: Plus, onClick: add } : null}
        />
      ) : (
        <>
        <Table data-testid="demarcation-table" wrapperClassName="hidden overflow-x-auto sm:block">
          <TableHeader>
            <TableRow>
              <TableHead>Wat</TableHead>
              <TableHead className="w-40">Klein onderhoud</TableHead>
              <TableHead className="w-40">Vervanging / groot</TableHead>
              <TableHead>Notitie</TableHead>
              {writable && <TableHead className="w-12"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={i}>
                <TableCell>
                  {writable ? <Input value={r.element} onChange={(e) => update(i, "element", e.target.value)} /> : r.element}
                </TableCell>
                <TableCell>
                  {writable ? (
                    <Select value={r.small_maintenance} onValueChange={(v) => update(i, "small_maintenance", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{PARTY.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (r.small_maintenance === "ons" ? "Ons" : "Verhuurder")}
                </TableCell>
                <TableCell>
                  {writable ? (
                    <Select value={r.replacement} onValueChange={(v) => update(i, "replacement", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{PARTY.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (r.replacement === "ons" ? "Ons" : "Verhuurder")}
                </TableCell>
                <TableCell>
                  {writable ? <Input value={r.note || ""} onChange={(e) => update(i, "note", e.target.value)} /> : r.note}
                </TableCell>
                {writable && (
                  <TableCell><Button variant="ghost" size="icon" onClick={() => remove(i)}><Trash2 size={18} /></Button></TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Phone: one card per element, fields stacked. A four-column editor in a
            sideways-scrolling table is not something you fill in on a phone. */}
        <div className="space-y-3 sm:hidden" data-testid="demarcation-cardlist">
          {rows.map((r, i) => (
            <div key={i} className="space-y-2 rounded border border-border p-3" data-testid={`demarcation-card-${i}`}>
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <Label className="text-label uppercase tracking-[0.07em] text-muted-foreground">Wat</Label>
                  {writable
                    ? <Input value={r.element} onChange={(e) => update(i, "element", e.target.value)} />
                    : <p className="text-body text-foreground">{r.element}</p>}
                </div>
                {writable && (
                  <Button variant="ghost" size="icon" className="mt-5" onClick={() => remove(i)}><Trash2 size={18} /></Button>
                )}
              </div>
              <div>
                <Label className="text-label uppercase tracking-[0.07em] text-muted-foreground">Klein onderhoud</Label>
                {writable ? (
                  <Select value={r.small_maintenance} onValueChange={(v) => update(i, "small_maintenance", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PARTY.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
                  </Select>
                ) : <p className="text-body text-foreground">{r.small_maintenance === "ons" ? "Ons" : "Verhuurder"}</p>}
              </div>
              <div>
                <Label className="text-label uppercase tracking-[0.07em] text-muted-foreground">Vervanging / groot</Label>
                {writable ? (
                  <Select value={r.replacement} onValueChange={(v) => update(i, "replacement", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PARTY.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
                  </Select>
                ) : <p className="text-body text-foreground">{r.replacement === "ons" ? "Ons" : "Verhuurder"}</p>}
              </div>
              <div>
                <Label className="text-label uppercase tracking-[0.07em] text-muted-foreground">Notitie</Label>
                {writable
                  ? <Input value={r.note || ""} onChange={(e) => update(i, "note", e.target.value)} />
                  : <p className="text-body text-foreground">{r.note}</p>}
              </div>
            </div>
          ))}
        </div>
        </>
      )}
      {writable && (
        <Button type="button" variant="secondary" size="sm" className="mt-3" data-testid="demarcation-add-row" onClick={add}>
          <Plus size={18} className="mr-2" /> Regel toevoegen
        </Button>
      )}
    </div>
  );
};

export const SettingsDemarcation = () => {
  const { canWrite } = useAuth();
  const writable = canWrite();
  const [rows, setRows] = useState([]);
  const [threshold, setThreshold] = useState(1500);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/demarcation");
      setRows(data.template || []);
      setThreshold(data.groot_onderhoud_drempel_ex_btw ?? 1500);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const { value: thr, error: thrError } = parseAmount(threshold);
    if (thrError) { toast.error(thrError); return; }
    try {
      await api.put("/settings/demarcation", {
        template: rows,
        groot_onderhoud_drempel_ex_btw: thr,
      });
      toast.success("\"Wie betaalt wat\" opgeslagen");
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Opslaan mislukt."));
    }
  };

  if (loading) return <p className="text-body text-muted-foreground">Laden...</p>;

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="mb-4 max-w-xs">
          <Label htmlFor="thr">Drempel groot onderhoud (excl. btw)</Label>
          <MoneyInput id="thr" testid="demarcation-threshold-input" value={threshold} onChange={setThreshold} disabled={!writable} />
          <p className="mt-1 text-data text-muted-foreground">Boven dit bedrag geldt de kolom "Vervanging / groot onderhoud".</p>
        </div>
        <DemarcationEditor rows={rows} onChange={setRows} writable={writable} />
        {writable && (
          <div className="mt-4">
            <Button data-testid="demarcation-save-button" onClick={save}>Opslaan</Button>
          </div>
        )}
      </Card>
    </div>
  );
};
